import React, { createContext, useContext, useEffect, useState } from "react";
import { isEurope } from "../lib/currency";

const CurrencyContext = createContext({ currency: "USD", ready: false });

const CACHE_KEY = "ae_currency_v1";

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState("USD");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // 1) Session cache
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached === "EUR" || cached === "USD") {
        setCurrency(cached);
        setReady(true);
        return;
      }
    } catch (_) {
      /* noop */
    }

    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);

    // 2) Try ipapi.co (HTTPS, no key, generous free tier)
    fetch("https://ipapi.co/json/", { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("bad_status"))))
      .then((d) => {
        if (cancelled) return;
        const eu = isEurope(d?.country_code || d?.country, d?.continent_code);
        const c = eu ? "EUR" : "USD";
        setCurrency(c);
        try {
          sessionStorage.setItem(CACHE_KEY, c);
        } catch (_) {
          /* noop */
        }
      })
      .catch(() => {
        // 3) Fallback: use browser timezone as a soft hint
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
          const looksEurope = /^Europe\//i.test(tz) || /^Atlantic\/(Canary|Madeira|Azores|Faroe)/i.test(tz);
          const c = looksEurope ? "EUR" : "USD";
          setCurrency(c);
          try {
            sessionStorage.setItem(CACHE_KEY, c);
          } catch (_) {
            /* noop */
          }
        } catch (_) {
          setCurrency("USD");
        }
      })
      .finally(() => {
        clearTimeout(timer);
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, ready }}>{children}</CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
